import type { RenderQualityId } from "./quality";

export type AdaptiveTier = Exclude<RenderQualityId, "auto">;

const TIER_ORDER: AdaptiveTier[] = ["low", "medium", "high"];

/**
 * Presentation-only frame-time scaler. Never touches simulation.
 * Drops a tier when EMA frame time stays poor; raises slowly when healthy.
 */
export class AdaptiveQualityController {
  private emaMs = 16.6;
  private poorFrames = 0;
  private goodFrames = 0;
  private cooldownFrames = 0;
  private current: AdaptiveTier;

  public constructor(
    initial: AdaptiveTier,
    private ceiling: AdaptiveTier,
  ) {
    this.current = initial;
  }

  public getTier(): AdaptiveTier {
    return this.current;
  }

  public reset(initial: AdaptiveTier, ceiling: AdaptiveTier): void {
    this.current = initial;
    this.ceiling = ceiling;
    this.emaMs = 16.6;
    this.poorFrames = 0;
    this.goodFrames = 0;
    this.cooldownFrames = 90;
  }

  /** dt in seconds from the render clock. Returns the new tier when it changes. */
  public sample(dtSeconds: number): AdaptiveTier | null {
    const dtMs = Math.min(80, Math.max(4, dtSeconds * 1000));
    this.emaMs = this.emaMs * 0.9 + dtMs * 0.1;
    if (this.cooldownFrames > 0) {
      this.cooldownFrames -= 1;
      return null;
    }

    if (this.emaMs > 22) {
      this.poorFrames += 1;
      this.goodFrames = 0;
      if (this.poorFrames >= 90) {
        const next = shiftTier(this.current, -1);
        if (next !== this.current) {
          this.current = next;
          this.poorFrames = 0;
          this.cooldownFrames = 180;
          return next;
        }
      }
    } else if (this.emaMs < 17.4) {
      this.goodFrames += 1;
      this.poorFrames = 0;
      if (this.goodFrames >= 300) {
        const next = shiftTier(this.current, 1);
        const capped = tierIndex(next) > tierIndex(this.ceiling) ? this.ceiling : next;
        if (capped !== this.current) {
          this.current = capped;
          this.goodFrames = 0;
          this.cooldownFrames = 240;
          return capped;
        }
        this.goodFrames = 0;
      }
    } else {
      this.poorFrames = 0;
      this.goodFrames = 0;
    }
    return null;
  }
}

function tierIndex(tier: AdaptiveTier): number {
  return TIER_ORDER.indexOf(tier);
}

function shiftTier(tier: AdaptiveTier, delta: number): AdaptiveTier {
  const next = Math.max(0, Math.min(TIER_ORDER.length - 1, tierIndex(tier) + delta));
  return TIER_ORDER[next]!;
}
